import React from 'react';

import { SynchronizeArrowsWarning } from 'src/app/components/icons/synchronize-arrows-warning';
import { Checkbox } from 'src/app/components/lib/checkbox';
import { DropdownMultiple } from 'src/app/components/lib/dropdown';
import { FormSection } from 'src/app/components/lib/form';
import { FormControl } from 'src/app/components/lib/form-control';
import { Input } from 'src/app/components/lib/input';
import { Label } from 'src/app/components/lib/label';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { Radio, RadioGroup } from 'src/app/components/lib/radio';
import { Select } from 'src/app/components/lib/select';
import { WizardCard } from 'src/app/components/lib/wizard/card';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import { ApplicationMethodTypeId, firstTimeCustomerOptions } from '../../../models';
import { useDiscountWizardStateContext } from '../../state';
import { DetailsStepActions } from '../../state/details';

import { LabelWithWarningIcon } from './label-with-warning-icon';
import { DescriptionWrapper, TextWrapper, Warning } from './styled';
import { useDetailsStepData } from './use-details-data';

export function EligibilityCard() {
  const {
    configureStepState: { applicationMethod },
    detailsStepState: {
      customerTypeIds,
      discountGroupIds,
      firstTimeCustomerOption,
      limitPerCustomer,
      limitPerCustomerQuantity,
      paymentRestrictions,
      redemptionLimit,
      requireManagerApproval,
      segmentIds,
    },
    detailsStepDispatch,
    isManualDiscount,
  } = useDiscountWizardStateContext();

  const {
    customerTypesDataLoading,
    customerTypesOptions,
    discountGroupOptions,
    discountGroupsDataLoading,
    segmentOptions,
    segmentsDataLoading,
  } = useDetailsStepData();

  const discountPaymentRestrictionsFlagEnabled = useLDFlag(LD.DISCOUNT_PAYMENT_RESTRICTIONS, false);

  const isCodeDiscount = applicationMethod === ApplicationMethodTypeId.ByCode;
  const isAutomaticDiscount = applicationMethod === ApplicationMethodTypeId.Automatically;

  const showPaymentRestrictionsField = discountPaymentRestrictionsFlagEnabled && isAutomaticDiscount;

  return (
    <WizardCard description={<EligibilityDescription />} title='Who is eligible'>
      <FormSection>
        <DropdownMultiple
          disabled={customerTypesDataLoading}
          helpText='All customer types'
          label='Customer type(s):'
          labelPlacement='top'
          options={customerTypesOptions}
          setValue={(newSelections) =>
            detailsStepDispatch({
              type: DetailsStepActions.SET_CUSTOMER_TYPES,
              payload: newSelections.map((selection) => Number(selection.id)),
            })
          }
          value={customerTypesOptions.filter((type) => customerTypeIds.includes(Number(type.id)))}
          withinModal
        />
        <DropdownMultiple
          disabled={discountGroupsDataLoading}
          helpText='All customer groups'
          label={<LabelWithWarningIcon label='Customer group(s)' />}
          labelPlacement='top'
          options={discountGroupOptions}
          setValue={(newSelections) =>
            detailsStepDispatch({
              type: DetailsStepActions.SET_DISCOUNT_GROUPS,
              payload: newSelections.map((selection) => Number(selection.id)),
            })
          }
          value={discountGroupOptions.filter((group) => discountGroupIds.includes(Number(group.id)))}
          withinModal
        />
        <DropdownMultiple
          helpText='All customer segments'
          label={<LabelWithWarningIcon label='Customer segment(s)' />}
          labelPlacement='top'
          loading={segmentsDataLoading}
          options={segmentOptions}
          setValue={(newSelections) => {
            detailsStepDispatch({
              type: DetailsStepActions.SET_SEGMENTS,
              payload: newSelections.map((selection) => Number(selection.id)),
            });
          }}
          tooltip='Restrict use of discount to customers in selected segments.'
          useV2Tooltip
          value={segmentOptions.filter((segment) => segmentIds.includes(Number(segment.id)))}
        />
        <RadioGroup
          label={<LabelWithWarningIcon label='Limit per customer' />}
          labelForId='Limit per customer'
          labelPlacement='top'
          variant='boolean'
          onChange={({ target }) =>
            detailsStepDispatch({
              type: DetailsStepActions.SET_LIMIT_PER_CUSTOMER,
              payload: target.value !== 'false',
            })
          }
        >
          <Radio checked={limitPerCustomer} label='Yes' value />
          <Radio checked={!limitPerCustomer} label='No' value={false} />
        </RadioGroup>
        {limitPerCustomer && (
          <Input
            label='Limit:'
            labelPlacement='top'
            required
            type='number'
            value={limitPerCustomerQuantity}
            onChange={({ target }) => {
              detailsStepDispatch({
                type: DetailsStepActions.SET_LIMIT_PER_CUSTOMER_QUANTITY,
                payload: target.value === '' ? '' : Number(target.value),
              });
            }}
          />
        )}
        <Select
          label={<LabelWithWarningIcon label='First time customer only' />}
          labelPlacement='top'
          value={firstTimeCustomerOption}
          onChange={({ target }) =>
            detailsStepDispatch({ type: DetailsStepActions.SET_FIRST_TIME_CUSTOMER_OPTION, payload: target.value })
          }
        >
          {firstTimeCustomerOptions.map((option) => (
            <MenuItem key={option.name} value={option.val}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        {showPaymentRestrictionsField && (
          <FormControl $labelPlacement='top'>
            <Label tooltip='Restrict use of discount to certain payment types.' useV2Tooltip>
              Payment restrictions:
            </Label>
            <Checkbox
              checked={paymentRestrictions.PayByBankSignupIncentive}
              label='Pay by Bank signup incentive'
              onChange={({ target }) => {
                detailsStepDispatch({
                  type: DetailsStepActions.SET_PAYMENT_RESTRICTION,
                  payload: { restriction: 'PayByBankSignupIncentive', enabled: target.checked },
                });
              }}
            />
          </FormControl>
        )}
        {isCodeDiscount && (
          <Input
            label='Redemption limit:'
            labelPlacement='top'
            tooltip='The total number of times this discount can be used before it expires.'
            type='number'
            useV2Tooltip
            value={redemptionLimit}
            onChange={({ target }) => {
              detailsStepDispatch({
                type: DetailsStepActions.SET_REDEMPTION_LIMIT,
                payload: target.value === '' ? '' : Number(target.value),
              });
            }}
          />
        )}
        {isManualDiscount && (
          <RadioGroup
            label='Require manager approval:'
            labelPlacement='top'
            variant='boolean'
            onChange={({ target }) =>
              detailsStepDispatch({
                type: DetailsStepActions.SET_REQUIRE_MANAGER_APPROVAL,
                payload: target.value !== 'false',
              })
            }
          >
            <Radio checked={requireManagerApproval} label='Yes' value />
            <Radio checked={!requireManagerApproval} label='No' value={false} />
          </RadioGroup>
        )}
      </FormSection>
    </WizardCard>
  );
}

function EligibilityDescription() {
  return (
    <DescriptionWrapper>
      <TextWrapper>
        <p>Specify who can receive the discount and limit how many times it can be redeemed.</p>
        <p>Choose to limit how many times the discount can be redeemed.</p>
      </TextWrapper>
      <Warning>
        <SynchronizeArrowsWarning />
        indicates that if you change this field from the default setting, this discount will not be shown online in your
        Dutchie Ecommerce store.
      </Warning>
    </DescriptionWrapper>
  );
}
